// WorkflowAddOutcomeModal.jsx

import React, { useEffect, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { WORKFLOW_OUTCOME_COMPLETE } from 'workflows/workflowGraphUtils';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import TextInput from 'components/textinput/TextInput';

/**
 * WorkflowAddOutcomeModal
 *
 * Alternate-path authoring only: Complete already continues from this task,
 * so the user names a different route (e.g. Bad Fit).
 * When `toTask` is set, the path connects to that existing task instead of
 * creating a new one.
 *
 * @returns {ReactNode}
 */
const WorkflowAddOutcomeModal = React.memo(({
  fromTask = null,
  isOpen = false,
  onClose,
  onSave,
  toTask = null,
}) => {
  const isMobile = useIsMobile();
  const [buttonLabel, setButtonLabel] = useState('');
  const [error, setError] = useState('');

  const fromTaskKey = String(fromTask?.key || '').trim();
  const toTaskKey = String(toTask?.key || '').trim();
  const connectsExisting = Boolean(toTaskKey);

  useEffect(() => {
    if (!isOpen) return;
    setButtonLabel('');
    setError('');
  }, [isOpen, fromTaskKey, toTaskKey]);

  const handleSave = () => {
    const cleanedLabel = String(buttonLabel || '').trim();
    if (!cleanedLabel) {
      setError('Name this path (for example, Bad Fit).');
      return;
    }
    if (cleanedLabel.toLowerCase() === WORKFLOW_OUTCOME_COMPLETE) {
      setError('Complete is already used for the main path. Choose a different name.');
      return;
    }
    onSave?.({ label: cleanedLabel });
  };

  if (!isOpen) return null;

  const fromTitle = String(fromTask?.title || '').trim() || 'this task';
  const toTitle = String(toTask?.title || '').trim() || 'the selected task';
  const hint = connectsExisting
    ? `Connect “${fromTitle}” to “${toTitle}” with a named path. Operators will see it as a button.`
    : `“${fromTitle}” already continues on Complete. Name this alternate route — operators will see it as a button.`;

  return (
    <Modal
      iconKey="alt_route"
      isOpen={isOpen}
      title="Add another path"
      width={isMobile ? '100%' : 420}
      onClose={() => onClose?.()}
    >
      <ModalForm>
        <ModalHint hint={hint} />

        <ModalField>
          <ModalLabel label="Path name" />
          <TextInput
            autoFocus
            error={Boolean(error)}
            placeholder="Bad Fit"
            value={buttonLabel}
            onChange={(event) => {
              setButtonLabel(String(event.target.value || ''));
              if (error) setError('');
            }}
            onEnterPress={handleSave}
          />
          {error ? (
            <ModalHint hint={error} />
          ) : null}
        </ModalField>
      </ModalForm>

      <ModalButtons>
        <ModalButtonSection align="END">
          <Button
            iconKey="close"
            label="Cancel"
            onClick={() => onClose?.()}
          />
          <Button
            iconKey={connectsExisting ? 'check' : 'arrow_forward'}
            label={connectsExisting ? 'Connect' : 'Next'}
            onClick={handleSave}
          />
        </ModalButtonSection>
      </ModalButtons>
    </Modal>
  );
});

WorkflowAddOutcomeModal.displayName = 'WorkflowAddOutcomeModal';

export default WorkflowAddOutcomeModal;
